import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import { toast } from 'sonner';
import type { AppActionHandler } from '@erp/json-renderer/apps/react';
import type { HarnessActionPendingResult } from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import { useChatThreadId } from '../chatThreadContext';
import type { ViewCommand } from './showViewState';

/**
 * The handlers behind a chat view's buttons. A press previews the command's
 * action as the user, in this conversation; nothing changes until they apply
 * the preview it returns. While one press is being prepared, others are ignored.
 */
export function useViewCommands(
  commands: Readonly<Record<string, ViewCommand>>,
): {
  handlers: Record<string, AppActionHandler>;
  previews: HarnessActionPendingResult[];
} {
  const gt = useGT();
  const threadId = useChatThreadId();
  const [previews, setPreviews] = useState<HarnessActionPendingResult[]>([]);
  const preview = useMutation(
    orpc.harness.actions.previewViewCommand.mutationOptions(),
  );

  const run = async (command: ViewCommand, params: Record<string, unknown>) => {
    if (preview.isPending) return;
    if (!threadId) {
      toast.error(
        gt('This button only works in a saved conversation.', {
          context:
            'chat: a button in an assistant view pressed outside a saved thread',
        }),
      );
      return;
    }
    try {
      const result = await preview.mutateAsync({
        threadId,
        tool: command.tool,
        input: command.input,
        params,
      });
      if (result.status === 'pending_user_approval') {
        setPreviews((current) => [...current, result]);
      } else if (result.status === 'failed') {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : gt("The button's action couldn't be prepared.", {
              context: 'chat: a button in an assistant view failed',
            }),
      );
    }
  };

  const handlers = Object.fromEntries(
    Object.entries(commands).map(([name, command]) => [
      name,
      (params: Record<string, unknown>) => run(command, params),
    ]),
  );
  return { handlers, previews };
}
